<script>
	import model from "../../stores/modelStore";
	import { registerPageComplete } from "../../tealium";
	import { projectSettings, yesNoUnsureOptions } from "../../config";
	import { isValid } from "../../validation";
	import lookups from "../../stores/lookupStore";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import NumberInput from "../../components/NumberInput.svelte";
	import Select from "../../components/Select.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import DatePicker from "../../components/DatePicker.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";
	import SocialMediaDetails from "./SocialMediaDetails.svelte";

	// props
	export let persistUrl = null;
	export let pageName = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	const createSocialMediaDetails = () => ({
		endYearDontKnow: false,
		endMonthDontKnow: false,
		startYearDontKnow: false,
		startMonthDontKnow: false,
		startMonth: null,
		startYear: null,
		endMonth: null,
		endYear: null,
		userName: null
	});

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	let buttons = null;

	function hasValue(value) {
		if (value == null || value === false) return false;
		if (typeof value === "string") return value.trim().length > 0;
		if (Array.isArray(value)) return value.some(hasValue);
		if (typeof value === "object") return Object.values(value).some(hasValue);
		return true;
	}

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();

		if (!errors.length) {

			// Remove any social media details that do not have a valid platform lookup ID before saving, 
			// because they did not have any details.
			// NOTE: This is now done within the RegistrationApiController.
			// $model.claimDetails.socialMediaDetails =
			// 	$model.claimDetails.socialMediaDetails.filter(
			// 		detail => Number(detail?.socialMediaPlatformLookupId) > 0
			// 	);

			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
			} else {
				await registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	// Ensure that the social media details array has an entry for each social media platform in the lookups, 
	// creating new entries as needed.
	$: if ($model.claimDetails && socialMediaPlatforms.length) {
		const current = $model.claimDetails.socialMediaDetails;
		const details = Array.isArray(current) ? current : [];
		let changed = current !== details;

		socialMediaPlatforms.forEach((_, index) => {
			if (details[index] == null) {
				details[index] = createSocialMediaDetails();
				changed = true;
			}
		});

		if (changed) {
			$model.claimDetails.socialMediaDetails = details;
		}
	}


	$: socialMediaPlatforms = ($lookups?.socialMediaPlatforms ?? [])
	.filter(platform => platform.label !== "Other");

	$: otherSocialMediaPlatform = ($lookups?.socialMediaPlatforms ?? [])
		.find(platform => platform.label === "Other");


	const initialise = () => {
		if (!$model.claimDetails) {
			$model.claimDetails = {
				socialMediaDetails: [],
				difficultiesReducingOrStoppingUses: []
			};
		}

		$model.claimDetails.socialMediaDetails ??= [];
		$model.claimDetails.difficultiesReducingOrStoppingUses ??= [];
	};

	initialise();


	// For treatment support services, check if "Other" is selected to determine 
	// whether to show the text area for additional details.
	$: otherTreatmentSupportServices = ($lookups?.treatmentSupportServices ?? [])
		.find(condition => condition.label === "Other");

	$: showTreatmentSupportServicesOther =
		!!otherTreatmentSupportServices &&
		($model.claimDetails?.treatmentSupportServices ?? []).some(
			selected => String(selected) === String(otherTreatmentSupportServices.value)
		);
</script>

<PageSection>
	<Form {disabled} id="form-claim-details" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Claim Details</h1>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRow>
				<FormColumn>
						<p><strong>Which social media platform(s) did you use?</strong></p>
						<p>Select all that apply</p>
				</FormColumn>
			</FormRow>

			{#each socialMediaPlatforms as platform, index (platform.value)}
				<SocialMediaDetails
					{disabled}
					label={platform.label}
					platformLookupId={platform.value}
					bind:socialMedialDetailsData={$model.claimDetails.socialMediaDetails[index]} />
			{/each}

			{#if otherSocialMediaPlatform}
				<SocialMediaDetails
					{disabled}
					label={otherSocialMediaPlatform.label}
					platformLookupId={otherSocialMediaPlatform.value}
					bind:otherTextValue={$model.claimDetails.otherSocialMediaPlatforms}
					showOtherButton={true} />
			{/if}

			

			<FormRow>
				<FormColumn cols="6">
					<NumberInput
						required
						{disabled}
						label="How old were you when you first used the platform(s)"
						bind:value={$model.claimDetails.ageWhenFirstUsed}
						minvalue="1"
						maxvalue="120" />
				</FormColumn>
			</FormRow>

			<FormRow>
				<FormColumn cols="12">
					<Select
						required
						{disabled}
						label="At the time of your highest use, approximately how long did you use the platform(s) each day?"
						bind:value={$model.claimDetails.averageUsageTimeId}
						optionList={$lookups.usageTimeRange}
						textProperty="label"
						valueProperty="value" />
				</FormColumn>
			</FormRow>

			<FormRow>
				<FormColumn cols="12">
					<Select
						required
						{disabled}
						label="How did your use change over time?"
						bind:value={$model.claimDetails.usageChangeOverTimeId}
						optionList={$lookups.usageChangeOverTimeTypes}
						textProperty="label"
						valueProperty="value" />
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn>
					<Select
						required
						{disabled}
						label="Did you ever try to reduce or stop your use of the platform(s)?"
						bind:value={$model.claimDetails.triedToReduceOrStopUse}
						optionList={yesNoUnsureOptions}
						textProperty="label"
						valueProperty="value" />
				</FormColumn>
			</FormRow>

			{#if $model.claimDetails.triedToReduceOrStopUse === "Yes"}
				<FormRow>
					<FormColumn cols="12">
						<Select
							required
							{disabled}
							label="Were you able to reduce or stop your use when you tried?"
							bind:value={$model.claimDetails.ableToReduceOrStopUseId}
							optionList={$lookups.ableToReduceOrStopUseTypes}
							textProperty="label"
							valueProperty="value" />
					</FormColumn>
				</FormRow>
			{/if}

			<FormRow>
				<FormColumn>
					<CheckboxButtonList
						name="difficultiesReducingOrStoppingUse"
						label="What, if anything, made it difficult to reduce or stop your use?"
						sublabel="Select all that apply"
						bind:selected={$model.claimDetails.difficultiesReducingOrStoppingUses}
						optionList={$lookups.difficultiesReducingOrStoppingUseTypes}
						isVertical={false}
						validator="checkboxRequired"
						validatorValue="1"
						validatorMessage="At least one selection is required"
						validatorTarget=".culture-error"
						labelProperty="label"
						valueProperty="value"
						{disabled}
					/>
				</FormColumn>
			</FormRow>


		</div>

		<FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>

<style>
</style>